import { css } from "@emotion/css";
import { borderWidth, space } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import type { PopoverBasicHelpProps } from "../../Popover/PopoverBasicHelp";
import { FormDescription } from "../Primitives/FormDescription";
import { InputError } from "../Primitives/InputError";
import { Legend } from "../Primitives/Legend";
import type { DescriptionContent } from "../utils/descriptionWithLinks";
export interface CheckboxGroupNewContextValue<T extends string | boolean = string | boolean> {
    value: T | T[];
    onChange: (value: T | T[]) => void;
    disabled?: boolean;
    error?: string;
    groupId: string;
    name?: string;
}
export const CheckboxGroupContext = React.createContext<CheckboxGroupNewContextValue<string | boolean> | undefined>(undefined);
export interface CheckboxNewGroupProps<T extends string | boolean = string | boolean> {
    /**
     * The label text to display for the checkbox group
     */
    label: string;
    /**
     * Optional description paragraph to display under the label.
     * Can be either:
     * - A simple string (basic usage)
     * - A DescriptionContent array created with descriptionText`...` for rich content with links and code
     * @example
     * // Simple string
     * description="This is a basic description."
     *
     * // Rich content with links and code
     * description={descriptionText`Set ${descriptionCode("isVisible")} prop or see ${descriptionLink("docs", "/docs")}.`}
     */
    description?: string | DescriptionContent;
    /**
     * The current value of the checkbox group as an array of selected values
     */
    value?: T[];
    /**
     * Callback function called when the checkbox group value changes
     * @param newValue - The new array of selected values
     */
    onChange?: (newValue: T[]) => void;
    /**
     * The checkbox components to render within this group (one or more CheckboxNew components)
     */
    children: React.ReactNode;
    /**
     * The name of the checkbox group, this is passed through to the individual checkboxes if no name is provided on the checkbox.
     */
    name?: string;
    /**
     * PopoverBasicHelp component to display additional help information
     * displays the Information icon next to the group label
     */
    popover?: React.ReactElement<PopoverBasicHelpProps>;
    /**
     * If true displays the (optional) text next to the group label
     */
    hasOptionalMarker?: boolean;
    /**
     * If true displays the (required) text next to the group label
     */
    hasRequiredMarker?: boolean;
    /**
     * Disables the checkbox group or individual checkbox
     */
    disabled?: boolean;
    /**
     * Error message to display.
     */
    error?: string;
    /**
     * These props hide the label and description visually and should be used when the checkbox group
     * is placed inside an expandable form section. Screen readers will still
     * announce them, but they won't be shown to users, since the content is
     * already displayed within the expandable section.
     *
     * Do not use these props if the checkbox group is outside an expandable form section.
     * Instead you should ensure the label and optional description is displayed.
     *
     * These props can be removed when expandable form sections are removed.
     */
    hideLabel?: boolean;
    hideDescription?: boolean;
}
export function CheckboxGroupNew<T extends string | boolean>(props: CheckboxNewGroupProps<T>) {
    const { description, children, error, hideLabel = false, hideDescription = false, hasOptionalMarker = false, hasRequiredMarker = false, popover, disabled, onChange, value, label, name } = props;
    const groupId = React.useId();
    const [hasInteracted, setHasInteracted] = React.useState(false);
    /* eslint-disable @typescript-eslint/consistent-type-assertions */
    const handleChange = React.useCallback((newValue: string | boolean | (string | boolean)[]) => {
        setHasInteracted(true);
        if (onChange && Array.isArray(newValue)) {
            onChange(newValue as T[]);
        }
    }, [onChange]);
    /* eslint-enable @typescript-eslint/consistent-type-assertions */
    const contextValue: CheckboxGroupNewContextValue<string | boolean> = React.useMemo(() => ({
        value: value ?? [],
        onChange: handleChange,
        disabled,
        groupId,
        error,
        name,
    }), [value, handleChange, disabled, groupId, error, name]);
    const showError = error && !hasInteracted;
    return (<CheckboxGroupContext.Provider value={contextValue}>
            <fieldset className={checkboxGroupStyles} role="group" disabled={disabled}>
                <Legend label={label} hasOptionalMarker={hasOptionalMarker} hasRequiredMarker={hasRequiredMarker} popover={popover} disabled={disabled} hideLabel={hideLabel}/>
                {description && <FormDescription description={description} isDisabled={disabled} isGroupLabel={true} hideDescription={hideDescription}/>}
                {children}
                {showError && (<div className={errorStyles}>
                        <InputError error={error} id={`${groupId}-error`}/>
                    </div>)}
            </fieldset>
        </CheckboxGroupContext.Provider>);
}
const checkboxGroupStyles = css({
    display: "grid",
    border: borderWidth.none,
    margin: 0,
    padding: 0,
});
const errorStyles = css({
    marginTop: space[4],
});
